
import React from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';
import { Database } from '@/integrations/supabase/types';
import { Avatar, AvatarImage } from "@/components/ui/avatar";
import { useIsMobile } from '@/hooks/use-mobile';

type Testimonial = Database['public']['Tables']['testimonials']['Row'];

const NewSectionFive = () => {
  const isMobile = useIsMobile();
  
  const { data: testimonials = [] } = useQuery({
    queryKey: ['testimonials'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('testimonials')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data as Testimonial[];
    },
  });

  const { data: images } = useQuery({
    queryKey: ["images", "section-five"],
    queryFn: async () => {
      console.log("Fetching images for section-five");
      const { data, error } = await supabase
        .from("images")
        .select("*")
        .eq("section", "section-five")
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error("Error fetching images:", error);
        throw error;
      }
      return data;
    },
  });

  const getBackgroundImage = () => {
    if (images && images.length > 0) {
      const { data } = supabase.storage
        .from('images')
        .getPublicUrl(images[0].storage_path);
      return data.publicUrl;
    }
    return 'https://images.unsplash.com/photo-1519389950473-47ba0277781c?auto=format&fit=crop&q=80';
  };

  const profileImages = [
    'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=200&h=200&fit=crop',
    'https://images.unsplash.com/photo-1581090464777-f3220bbe1b8b?w=200&h=200&fit=crop',
    'https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?w=200&h=200&fit=crop'
  ];

  return (
    <div className="bg-gradient-to-r from-[#243949] to-[#517fa4]">
      <div className="container mx-auto px-4 py-8 md:py-16">
        <h3 className="text-lg md:text-xl font-bold text-white mb-8 text-center uppercase">
          Erfahrungsberichte
        </h3>
        
        {isMobile ? (
          <div className="flex flex-col md:flex-row md:items-center md:space-x-8">
            <div className="w-full md:w-1/2 mb-6 md:mb-0">
              <div className="bg-white/10 backdrop-blur-sm p-4 rounded-lg shadow-lg border border-white/20">
                <div className="relative aspect-[16/9] w-full">
                  <img
                    src={getBackgroundImage()}
                    alt="Erfahrungsberichte"
                    className="rounded-lg object-cover w-full h-full border-2 border-white/40"
                  />
                </div>
              </div>
            </div>

            <div className="w-full md:w-1/2 space-y-4">
              {testimonials.map((testimonial, index) => (
                <div key={testimonial.id} className="group relative bg-white/10 backdrop-blur-sm py-2.5 px-4 rounded-lg shadow-lg border border-white/20 ml-6">
                  <div className="absolute left-0 top-1/2 -translate-x-1/2 -translate-y-1/2">
                    <div className="w-12 h-12 rounded-full bg-[#8E9196] flex items-center justify-center overflow-hidden">
                      <Avatar className="w-full h-full">
                        <AvatarImage 
                          src={profileImages[index % profileImages.length]} 
                          alt={testimonial.author}
                          className="object-cover"
                        />
                      </Avatar>
                    </div>
                  </div>
                  <div className="text-left">
                    <p className="text-base md:text-lg text-white pl-4">
                      {testimonial.content}
                    </p>
                    <p className="text-xs md:text-sm text-white/80 pl-4 mt-2 uppercase font-bold">
                      {testimonial.author}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center">
            {/* Header image now has max-w-xl to match NS1 and NS2 size */}
            <div className="w-full max-w-xl mb-8">
              <div className="bg-white/10 backdrop-blur-sm p-4 rounded-lg shadow-lg border border-white/20">
                <div className="relative aspect-[16/9] w-full">
                  <img
                    src={getBackgroundImage()}
                    alt="Erfahrungsberichte"
                    className="rounded-lg object-cover w-full h-full border-2 border-white/40"
                  />
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
              {testimonials.map((testimonial, index) => (
                <div key={testimonial.id} className="group relative bg-white/10 backdrop-blur-sm py-2.5 px-4 rounded-lg shadow-lg border border-white/20 ml-6">
                  <div className="absolute left-0 top-1/2 -translate-x-1/2 -translate-y-1/2">
                    <div className="w-12 h-12 rounded-full bg-[#8E9196] flex items-center justify-center overflow-hidden">
                      <Avatar className="w-full h-full">
                        <AvatarImage 
                          src={profileImages[index % profileImages.length]} 
                          alt={testimonial.author}
                          className="object-cover"
                        />
                      </Avatar>
                    </div>
                  </div>
                  <div className="text-left">
                    <p className="text-base md:text-lg text-white pl-4">
                      {testimonial.content}
                    </p>
                    <p className="text-xs md:text-sm text-white/80 pl-4 mt-2 uppercase font-bold">
                      {testimonial.author}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NewSectionFive;
